<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { KeyRound, RefreshCw } from "lucide-vue-next";
import type { ExternalMfaChallenge } from "@/services/firebase";

const props = withDefaults(
  defineProps<{
    challenge: ExternalMfaChallenge;
    busy?: boolean;
    error?: string;
    actionLabel?: string;
  }>(),
  {
    busy: false,
    error: "",
    actionLabel: "Verify and sign in",
  },
);

const emit = defineEmits<{
  verify: [value: { factorUid: string; code: string }];
  cancel: [];
}>();

const code = ref("");
const selectedFactorUid = ref(props.challenge.factors[0]?.uid || "");
const validCode = computed(() => /^\d{6}$/.test(code.value));

watch(
  () => props.challenge,
  (challenge) => {
    code.value = "";
    selectedFactorUid.value = challenge.factors[0]?.uid || "";
  },
);

function submit() {
  if (!validCode.value || !selectedFactorUid.value || props.busy) return;
  emit("verify", {
    factorUid: selectedFactorUid.value,
    code: code.value,
  });
}
</script>

<template>
  <form class="mfa-form" @submit.prevent="submit">
    <div class="mfa-heading">
      <span class="mfa-icon" aria-hidden="true"><KeyRound :size="20" /></span>
      <div>
        <h3>Verify it’s you</h3>
        <p>Enter the 6-digit code from your authenticator app.</p>
      </div>
    </div>

    <div v-if="challenge.factors.length > 1" class="field">
      <label for="mfa-factor">Authenticator app</label>
      <select id="mfa-factor" v-model="selectedFactorUid" class="input">
        <option
          v-for="factor in challenge.factors"
          :key="factor.uid"
          :value="factor.uid"
        >
          {{ factor.displayName }}
        </option>
      </select>
    </div>

    <div class="field">
      <label for="mfa-code">Verification code</label>
      <input
        id="mfa-code"
        v-model="code"
        class="input mfa-code"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        pattern="[0-9]{6}"
        maxlength="6"
        aria-describedby="mfa-code-help"
        autofocus
        required
      />
      <p id="mfa-code-help" class="helper">
        Codes change every 30 seconds. Use the code currently shown in your app.
      </p>
    </div>

    <p v-if="error" class="mfa-error" role="alert">{{ error }}</p>

    <button
      class="button button--primary mfa-submit"
      type="submit"
      :disabled="busy || !validCode || !selectedFactorUid"
    >
      <RefreshCw v-if="busy" class="spin" :size="16" />
      {{ busy ? "Verifying…" : actionLabel }}
    </button>
    <button
      class="text-action mfa-back"
      type="button"
      :disabled="busy"
      @click="emit('cancel')"
    >
      Back
    </button>
  </form>
</template>

<style scoped>
.mfa-form {
  display: grid;
  gap: 16px;
}
.mfa-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 4px;
}
.mfa-heading h3 {
  font-size: 18px;
}
.mfa-heading p,
.helper {
  margin-top: 4px;
  color: var(--text-soft);
}
.mfa-icon {
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--brand-strong);
  background: var(--brand-soft);
}
.mfa-form .input {
  min-height: 44px;
}
.mfa-code {
  max-width: 12rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.18em;
}
.mfa-error {
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  color: var(--danger);
  background: var(--danger-soft);
}
.mfa-submit {
  min-height: 44px;
}
.mfa-back {
  min-height: 44px;
  justify-self: center;
}
.spin {
  animation: spin 900ms linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .spin {
    animation: none;
  }
}
</style>
